import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, SkipForward, SkipBack, Music, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useSession } from '../../context/SessionContext';
import { useMusicKit } from '../../context/MusicKitContext';
import { usePlaylistMusic } from '../../context/PlaylistMusicContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import MusicSearchModal from '../sessions/MusicSearchModal';
import FireEmoji from './FireEmoji';

// Import fingerprint SVG
import FingerprintIcon from '../../assets/fingerprint-icon.svg';

/**
 * PersistentMiniPlayer - "FULL SONG BAR" design (Session version)
 * Used for: Active Sessions (when not viewing session screen)
 * Features: Queue toggle, Fire emoji, Fingerprint (tap-in), Play controls
 * Reference name: "Full Song Bar" or "Session Bar"
 */
const PersistentMiniPlayer = ({ onTap }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [showQueue, setShowQueue] = useState(false); // Queue toggle state
  const { 
    activeSessionData, 
    isInActiveSession, 
    isPlaying: sessionIsPlaying, 
    togglePlayPause: sessionTogglePlayPause,
    getCurrentTrack,
    getNextTrack,
    getPreviousTrack,
    skipNext: sessionSkipNext,
    skipPrevious: sessionSkipPrevious,
    songQueue,
    currentTrackIndex,
    registerStopMusicCallback,
    wsConnected,
    fireCount: sessionFireCount = 0,
    sendFireReaction,
    firedSongIds = new Set(),
    addToQueue,
  } = useSession();

  const {
    isConfigured: musicKitConfigured,
    isAuthorized: musicKitAuthorized,
    isPlaying: musicKitIsPlaying,
    currentSong: musicKitCurrentSong,
    togglePlayPause: musicKitTogglePlayPause,
    playSong,
    stop: stopMusicKit,
    setOnTrackEndedCallback,
    callbackVersion,
    playbackTime,
    duration,
    seekTo,
  } = useMusicKit();

  const { hasPlaylist, togglePlayPause: playlistTogglePlayPause } = usePlaylistMusic();

  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('tapInMiniPlayerMinimized') === 'true';
    } catch {
      return false;
    }
  });
  
  const [fireAnimation, setFireAnimation] = useState(false);
  const [showMusicSearch, setShowMusicSearch] = useState(false);
  const [localFiredSongs, setLocalFiredSongs] = useState(new Set());

  useEffect(() => {
    try {
      localStorage.setItem('tapInMiniPlayerMinimized', isMinimized.toString());
    } catch {}
  }, [isMinimized]);

  const isUsingMusicKit = musicKitConfigured && musicKitAuthorized;
  const isPlaying = isUsingMusicKit ? musicKitIsPlaying : sessionIsPlaying;

  // Refs for auto-play
  const songQueueRef = useRef(songQueue);
  const currentTrackIndexRef = useRef(currentTrackIndex);
  const sessionSkipNextRef = useRef(sessionSkipNext);
  const playSongRef = useRef(playSong);
  const activeSessionDataRef = useRef(activeSessionData);
  // Skip operation lock — prevents concurrent skip operations from rapid clicks
  const isSkippingRef = useRef(false);
  
  useEffect(() => {
    songQueueRef.current = songQueue;
    currentTrackIndexRef.current = currentTrackIndex;
    sessionSkipNextRef.current = sessionSkipNext;
    playSongRef.current = playSong;
    activeSessionDataRef.current = activeSessionData;
  });

  // Register stop callback
  useEffect(() => {
    if (!registerStopMusicCallback || !stopMusicKit) return;
    const unregister = registerStopMusicCallback(async () => {
      try {
        await stopMusicKit();
      } catch (err) {
        console.warn('[MiniPlayer] Error stopping MusicKit:', err.message);
      }
    });
    return () => unregister();
  }, [registerStopMusicCallback, stopMusicKit]);
  
  // Auto-play next song
  useEffect(() => {
    if (!setOnTrackEndedCallback || !isInActiveSession || !wsConnected) return;
    
    const handleTrackEnded = async () => {
      const queue = songQueueRef.current || [];
      const currentIndex = currentTrackIndexRef.current || 0;
      const session = activeSessionDataRef.current;
      
      const userId = localStorage.getItem('tapInUser');
      let isHost = false;
      try {
        const userObj = userId ? JSON.parse(userId) : null;
        isHost = session?.host_id === userObj?.id;
      } catch {}
      
      if (!isHost) return;
      
      const nextIndex = currentIndex + 1;
      if (nextIndex < queue.length) {
        const nextTrack = queue[nextIndex];
        if (nextTrack?.id) {
          sessionSkipNextRef.current?.();
          await new Promise(resolve => setTimeout(resolve, 100));
          try {
            await playSongRef.current?.(nextTrack.id);
          } catch {}
        }
      }
    };
    
    setOnTrackEndedCallback(handleTrackEnded);
  }, [setOnTrackEndedCallback, isInActiveSession, wsConnected, callbackVersion]);

  const currentTrack = getCurrentTrack();
  
  // Check if user is host
  const isHost = user?.id && activeSessionData?.host_id && user.id === activeSessionData.host_id;
  const isGuest = !isHost;
  
  // Check if tap-in is allowed
  const isTapInAllowed = activeSessionData?.allow_tap_in !== false;
  const canUseTapIn = isHost || isTapInAllowed;

  // Check if user has fired current song
  const currentSongId = currentTrack?.id || musicKitCurrentSong?.id;
  const hasUserFiredCurrentSong = currentSongId ? (firedSongIds.has(currentSongId) || localFiredSongs.has(currentSongId)) : false;
  
  // Display song
  const displaySong = useMemo(() => {
    if (isUsingMusicKit && musicKitCurrentSong) {
      let artworkUrl = musicKitCurrentSong.artworkUrl || '';
      if (artworkUrl && artworkUrl.includes('{w}')) {
        artworkUrl = artworkUrl.replace('{w}', '300').replace('{h}', '300');
      }
      return {
        title: musicKitCurrentSong.name,
        artist: musicKitCurrentSong.artistName,
        artwork_url: artworkUrl,
        id: musicKitCurrentSong.id
      };
    }
    if (currentTrack) {
      let artworkUrl = currentTrack.artwork_url || currentTrack.artworkUrl || '';
      if (artworkUrl && artworkUrl.includes('{w}')) {
        artworkUrl = artworkUrl.replace('{w}', '300').replace('{h}', '300');
      }
      return { ...currentTrack, artwork_url: artworkUrl };
    }
    return null;
  }, [isUsingMusicKit, musicKitCurrentSong, currentTrack]);

  // Progress bar
  const progressPercent = duration > 0 ? (playbackTime / duration) * 100 : 0;
  
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Handlers
  const handleTogglePlayPause = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (hasPlaylist && playlistTogglePlayPause) {
      playlistTogglePlayPause();
      return;
    }
    if (isUsingMusicKit) {
      musicKitTogglePlayPause();
    } else {
      sessionTogglePlayPause();
    }
  }, [isUsingMusicKit, musicKitTogglePlayPause, sessionTogglePlayPause, hasPlaylist, playlistTogglePlayPause]);

  const handleSkipNext = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (isSkippingRef.current) return;
    isSkippingRef.current = true;
    sessionSkipNext();
    if (isUsingMusicKit) {
      setTimeout(() => {
        const nextTrack = getNextTrack();
        if (nextTrack?.id) playSong(nextTrack.id);
      }, 150);
    }
    setTimeout(() => { isSkippingRef.current = false; }, 300);
  }, [sessionSkipNext, isUsingMusicKit, getNextTrack, playSong]);

  const handleSkipPrevious = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (isSkippingRef.current) return;
    isSkippingRef.current = true;
    sessionSkipPrevious();
    if (isUsingMusicKit) {
      setTimeout(() => {
        const prevTrack = getPreviousTrack();
        if (prevTrack?.id) playSong(prevTrack.id);
      }, 150);
    }
    setTimeout(() => { isSkippingRef.current = false; }, 300);
  }, [sessionSkipPrevious, isUsingMusicKit, getPreviousTrack, playSong]);

  const handleSeek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (seekTo && duration > 0) {
      seekTo(newTime);
    }
  }, [seekTo, duration]);

  const handleNavigateToSession = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onTap();
  }, [onTap]);

  // Fire emoji handler
  const handleFireReaction = useCallback(async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (!currentSongId || hasUserFiredCurrentSong) return;
    
    setFireAnimation(true);
    setTimeout(() => setFireAnimation(false), 500);
    
    setLocalFiredSongs(prev => new Set([...prev, currentSongId]));
    
    if (sendFireReaction) {
      try {
        await sendFireReaction(currentSongId);
      } catch (err) {
        console.error('[MiniPlayer] Fire reaction error:', err);
      }
    }
  }, [currentSongId, hasUserFiredCurrentSong, sendFireReaction]);

  // Fingerprint (Tap In) handler
  const handleTapIn = useCallback((e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!canUseTapIn) return;
    setShowMusicSearch(true);
  }, [canUseTapIn]);

  const handleAddSong = useCallback(async (song) => {
    console.log('[PersistentMiniPlayer] handleAddSong called:', song);
    if (!song || !song.id) {
      console.error('[PersistentMiniPlayer] Invalid song - missing id');
      setShowMusicSearch(false);
      return;
    }
    if (addToQueue && activeSessionData?.id) {
      try {
        await addToQueue(activeSessionData.id, song);
      } catch (err) {
        console.error('[PersistentMiniPlayer] Error adding song:', err);
      }
    }
    setShowMusicSearch(false);
  }, [addToQueue, activeSessionData?.id]);

  // Don't show if not in session
  if (!isInActiveSession || !activeSessionData || !wsConnected) return null;
  if (!displaySong || !displaySong.title) return null;

  // Minimized view - never show default music note
  if (isMinimized) {
    if (!displaySong?.artwork_url) return null;
    return (
      <div className="fixed bottom-6 right-4 z-50" data-testid="session-mini-player-minimized">
        <button
          onClick={() => setIsMinimized(false)}
          className="w-11 h-11 rounded-full overflow-hidden transition-all hover:scale-110 active:scale-95 relative"
          style={{
            boxShadow: isDarkMode 
              ? '0 0 15px rgba(168, 85, 247, 0.5), 0 0 30px rgba(168, 85, 247, 0.2)' 
              : '0 0 15px rgba(168, 85, 247, 0.4)',
          }}
        >
          <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
          {isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="flex gap-0.5">
                <div className="w-0.5 h-2.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="persistent-mini-player">
        <div className="max-w-[430px] mx-auto px-3">
          <div 
            className={`mb-3 rounded-2xl overflow-visible relative`}
            style={{
              background: isDarkMode ? 'rgba(55, 55, 60, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              boxShadow: isDarkMode 
                ? '0 0 20px rgba(168, 85, 247, 0.3), 0 -2px 15px rgba(0,0,0,0.3)' 
                : '0 -4px 20px rgba(0,0,0,0.1)',
            }}
          >
            {/* Queue Toggle Bar - Tap to expand/collapse */}
            <button
              onClick={() => setShowQueue(!showQueue)}
              className={`w-full px-2 py-0.5 flex items-center justify-center gap-1 border-b transition-all ${
                isDarkMode 
                  ? 'border-gray-700/50 hover:bg-white/5' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="session-queue-toggle"
            >
              <span className={`text-[12px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Up Next
              </span>
              <span className={`text-[9px] px-1 rounded ${
                isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {songQueue?.length || 0}
              </span>
              {showQueue ? (
                <ChevronDown className={`w-2.5 h-2.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <ChevronUp className={`w-2.5 h-2.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </button>

            {/* Expandable Queue List */}
            {showQueue && (
              <div className={`max-h-[180px] overflow-y-auto ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                {(!songQueue || songQueue.length === 0) ? (
                  <div className="py-3 text-center">
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No songs in queue</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {songQueue.map((song, index) => {
                      const isCurrentSong = index === currentTrackIndex;
                      return (
                        <div
                          key={song.id || index}
                          className={`flex items-center gap-2 px-3 py-1.5 ${
                            isCurrentSong
                              ? isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                              : ''
                          }`}
                        >
                          <span className={`w-4 text-center text-xs ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            {isCurrentSong ? '▶' : index + 1}
                          </span>
                          <div className="w-7 h-7 rounded overflow-hidden flex-shrink-0 bg-gray-600">
                            {song.artwork_url ? (
                              <img src={song.artwork_url.replace('{w}', '100').replace('{h}', '100')} alt={song.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Music className="w-3 h-3 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {song.title || song.name}
                            </p>
                            <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {song.artist || song.artistName}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* X button - small circle top left */}
            <button 
              onClick={() => setIsMinimized(true)}
              className={`absolute -top-2 -left-1 w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                isDarkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 active:bg-gray-500' 
                  : 'bg-gray-300 hover:bg-gray-400 text-gray-600 active:bg-gray-500'
              }`}
              style={{ touchAction: 'manipulation' }}
              data-testid="session-mini-player-close"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
            
            <div className="px-2 py-2 flex items-center gap-1.5">
              {/* Fire Emoji - LEFT side - ANY user can tap, 1x per song - New Gradient Design */}
              <FireEmoji
                size="md"
                count={sessionFireCount || 0}
                isActive={hasUserFiredCurrentSong}
                isAnimating={fireAnimation}
                onClick={handleFireReaction}
                disabled={hasUserFiredCurrentSong}
                isDarkMode={isDarkMode}
                className="ml-3 flex-shrink-0"
              />

              {/* Album art */}
              <button 
                onClick={handleNavigateToSession}
                className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 hover:scale-105 transition-transform"
              >
                {displaySong?.artwork_url ? (
                  <img src={displaySong.artwork_url} alt={displaySong?.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-600 rounded-lg flex items-center justify-center">
                    <Music className="w-4 h-4 text-gray-400" />
                  </div>
                )}
              </button>
              
              {/* Song info */}
              <button onClick={handleNavigateToSession} className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
                <p className={`font-medium truncate text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {displaySong?.title || 'No song playing'}
                </p>
                <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {displaySong?.artist || ''}
                </p>
              </button>
              
              {/* Previous - Guests disabled */}
              <button 
                onClick={handleSkipPrevious}
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  isGuest ? 'opacity-30 cursor-not-allowed' : `active:scale-90 ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`
                }`}
                disabled={isGuest}
                data-testid="mini-player-prev"
              >
                <SkipBack className={`w-3 h-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="currentColor" />
              </button>
              
              {/* Play/Pause - Guests disabled */}
              <button 
                onClick={handleTogglePlayPause}
                className={`w-8 h-8 aspect-square rounded-full flex items-center justify-center flex-shrink-0 transition-all bg-[#6366f1] text-white ${
                  isGuest ? 'opacity-50 cursor-not-allowed' : 'active:scale-90'
                }`}
                style={{ minWidth: '32px', minHeight: '32px' }}
                disabled={isGuest}
                data-testid="mini-player-play-pause"
              >
                {isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
              </button>
              
              {/* Next - Guests disabled */}
              <button 
                onClick={handleSkipNext}
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  isGuest ? 'opacity-30 cursor-not-allowed' : `active:scale-90 ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`
                }`}
                disabled={isGuest}
                data-testid="mini-player-next"
              >
                <SkipForward className={`w-3 h-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="currentColor" />
              </button>

              {/* Fingerprint - RIGHT side (Host only, if allowed) */}
              <button 
                onClick={handleTapIn}
                disabled={!canUseTapIn}
                className={`w-8 h-8 rounded-full overflow-hidden flex-shrink-0 transition-all ${
                  canUseTapIn 
                    ? 'hover:scale-110 active:scale-95' 
                    : 'opacity-30 cursor-not-allowed grayscale'
                }`}
                data-testid="session-mini-player-tapin"
                title={!canUseTapIn ? 'Tap In not allowed' : 'Add a song'}
              >
                <img src={FingerprintIcon} alt="Tap In" className="w-full h-full object-contain" />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="px-2 pb-1.5">
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(playbackTime)}
                </span>
                <div 
                  className={`flex-1 h-1 rounded-full cursor-pointer ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                  onClick={handleSeek}
                  data-testid="session-progress-bar"
                >
                  <div 
                    className={`h-full rounded-full transition-all duration-100 ${isDarkMode ? 'bg-white' : 'bg-gray-900'}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className={`text-[9px] font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Music Search Modal */}
      {showMusicSearch && (
        <MusicSearchModal 
          isOpen={showMusicSearch}
          onClose={() => setShowMusicSearch(false)}
          onAddSong={handleAddSong}
          context="session"
          isHost={isHost}
          session={activeSessionData}
          queue={songQueue}
          currentUserId={user?.id}
        />
      )}
    </>
  );
};

export default PersistentMiniPlayer;
